import React from "react";
import { View, StyleSheet, Pressable } from "react-native";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import { useEffect, useState } from "react";
import { LeftArrow } from "../../../components/svgs/common";
import CourseRosterTable from "./CourseRosterTable";
import { MinusCircleIcon, PlusCircle } from "../../../components/svgIcons";
import CustomButton from "../../../components/common/general/CustomButton/CustomButton";
import {
  useGetCourseByIdQuery,
  useGetStudentsByCourseIdQuery,
  useInviteUsersToEnrollInCourseMutation,
  useRemoveStudentsFromCourseMutation,
} from "../../../graphql/generated/graphql";
import NewCustomAxioActionModal from "../../settings/UsersTabContainer/NewCustomAxioActionModal";
import InviteStudentModalContainer from "./InviteStudentModalContainer";
import Pill from "../../../components/common/Pill/Pill";
import { CourseTypes } from "../../settings/CoursesTabContainer/CoursesTable";
import usePagination from "../../../hooks/usePagination";
import { Recycle } from "@phosphor-icons/react";
import TableControlsContainer from "../../general/TableControls/TableControlsContainer";
import SortingDropdown from "../../../components/common/TableControls/SortingDropdown/SortingDropdown";
import CustomSearchInput from "../../../components/common/general/CustomSearchInput/CustomSearchInput";
import useSortingDropdown, { SORT_ITEMS } from "../../../hooks/useSortingDropdown";

const modals = {
  removeStudents: {
    title: "Remove Selected Students from the Course?",
    ariaLabel: "Remove Students From Course Modal",
    warningText:
      "This action cannot be undone. These students will lose access to course materials and activities immediately.",
    actionButtonText: "Remove Students",
  },
  removeConfirmation: {
    title: "Students Removed",
    ariaLabel: "Students Removed Modal",
    warningText: "The selected students have been removed from the course.",
    actionButtonText: "OK",
  },
  resendInvites: {
    title: "Resend Invites",
    ariaLabel: "Resend Invites Modal",
    warningText: "The selected students will receive an email with a link to the course.",
    actionButtonText: "Resend Invites",
  },
  resendInvitesConfirmation: {
    title: "Invites Resent",
    ariaLabel: "Invites Resent Modal",
    warningText: "The selected students will receive an email with a link to the course.",
    actionButtonText: "OK",
  },
};

export enum RosterManagementModalState {
  CLOSED = "CLOSED",
  INVITE_STUDENTS = "INVITE_STUDENTS",
  REMOVE_STUDENTS = "REMOVE_STUDENTS",
  REMOVE_CONFIRMATION = "REMOVE_CONFIRMATION",
  RESEND_INVITES = "RESEND_INVITES",
  RESEND_INVITES_CONFIRMATION = "RESEND_INVITES_CONFIRMATION",
}

interface RosterManagementContainerProps {
  courseId: string;
  onBack?: () => void;
}

const RosterManagementContainer: React.FC<RosterManagementContainerProps> = ({ courseId, onBack }) => {
  const { data: courseData } = useGetCourseByIdQuery({
    variables: {
      id: courseId,
    },
  });

  const course = courseData?.getCourseById.data?.[0];

  const [isSubmitting, setIsSubmitting] = useState(false);
  const courseType = CourseTypes[course?.courseType] || CourseTypes.General;

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStudents, setSelectedStudents] = useState<string[]>([]);
  const [modalState, setModalState] = useState<{
    type: RosterManagementModalState;
  }>({
    type: RosterManagementModalState.CLOSED,
  });

  const { sortBy, setSortBy, sortDropdownOpen, setSortDropdownOpen } = useSortingDropdown({
    defaultSortBy: "name_asc",
  });

  const { page, setPage, pageSize, setPageSize } = usePagination({});

  const { data: getStudentsByCourseIdData, refetch: getStudentsByCourseIdRefetch } = useGetStudentsByCourseIdQuery({
    variables: {
      courseId: course?.id,
      query: searchQuery,
      page,
      pageSize,
      sortBy,
    },
    skip: !course?.id,
  });

  const totalItemsCount = getStudentsByCourseIdData?.getStudentsByCourseId.count || 0;

  const [inviteUsersToEnrollInCourse] = useInviteUsersToEnrollInCourseMutation();
  const [removeStudentsFromCourse] = useRemoveStudentsFromCourseMutation();

  const inviteStudents = async (students: { name: string; email: string }[]) => {
    setIsSubmitting(true);
    try {
      const result = await inviteUsersToEnrollInCourse({
        variables: {
          input: {
            courseId: course?.id,
            users: students,
          },
        },
      });

      if (result.data?.inviteUsersToEnrollInCourse.success) {
        getStudentsByCourseIdRefetch();
        setIsSubmitting(false);
        return true;
      }
      setIsSubmitting(false);
      return false;
    } catch (error) {
      setIsSubmitting(false);
      return false;
    }
  };

  const handleRemoveStudents = async () => {
    if (!selectedStudents.length) return;

    try {
      await removeStudentsFromCourse({
        variables: {
          courseId: course?.id,
          studentIds: selectedStudents,
        },
      });
      getStudentsByCourseIdRefetch();
      setSelectedStudents([]);
      setModalState({ type: RosterManagementModalState.REMOVE_CONFIRMATION });
    } catch (error) {
      console.error("Error removing students:", error);
    }
  };

  const handleResendInvites = async () => {
    const students = getStudentsByCourseIdData?.getStudentsByCourseId.data.filter((student) =>
      selectedStudents.includes(student.id)
    );
    await inviteStudents(students?.map((student) => ({ name: student.name, email: student.email })) || []);
    setModalState({ type: RosterManagementModalState.RESEND_INVITES_CONFIRMATION });
  };

  const handleModalClose = () => {
    setModalState({ type: RosterManagementModalState.CLOSED });
  };

  useEffect(() => {
    setPage(1);
  }, [searchQuery, sortBy]);

  useEffect(() => {
    getStudentsByCourseIdRefetch();
  }, [searchQuery, page, pageSize, sortBy]);

  if (!course) return null;

  return (
    <>
      <View style={styles.container}>
        <View style={styles.header}>
          {onBack && (
            <Pressable style={styles.backButton} onPress={onBack}>
              <LeftArrow />
              <CustomText text="Back" size="m" weight="semiBold" style={styles.backText} onPress={onBack} />
            </Pressable>
          )}
          <View style={styles.headerRow}>
            <View style={styles.subHeaderContainer}>
              <Pill
                text={courseType.name}
                leftIcon={courseType.icon}
                backgroundColor={courseType.color}
                textColor={courseType.textColor}
              />
              <CustomText
                text={course.name}
                size="l"
                weight="bold"
                style={styles.headerText}
                useTranslationText={false}
              />
            </View>
            <CustomButton
              leftIcon={<PlusCircle />}
              text="Invite Students"
              size="s"
              weight="bold"
              styleType="primaryTransparentStroke"
              onPress={() => setModalState({ type: RosterManagementModalState.INVITE_STUDENTS })}
              style={{ width: "fit-content" }}
              rightIcon={null}
              textStyle={{ fontWeight: "bold" }}
              textProps={{}}
            />
          </View>
          <View style={styles.headerRow}>
            <View style={styles.subHeaderContainer}>
              <CustomText text="Total Students" size="m" weight="semiBold" style={styles.subHeaderText} />
              <CustomText
                text={`(${getStudentsByCourseIdData?.getStudentsByCourseId.count || 0})`}
                size="m"
                weight="semiBold"
                style={styles.subHeaderText}
                useTranslationText={false}
              />
              {selectedStudents.length > 0 && (
                <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
                  <CustomButton
                    text={"Remove Selected"}
                    styleType="danger"
                    onPress={() => setModalState({ type: RosterManagementModalState.REMOVE_STUDENTS })}
                    leftIcon={<MinusCircleIcon />}
                    rightIcon={null}
                    style={{ gap: 8 }}
                    textStyle={{ color: "#fff", fontWeight: "bold" }}
                    disabled={false}
                    textProps={{}}
                  />
                  <CustomButton
                    text={"Re-Send Invites"}
                    styleType="primary"
                    onPress={() => setModalState({ type: RosterManagementModalState.RESEND_INVITES })}
                    leftIcon={<Recycle size={24} color="#fff" />}
                    rightIcon={null}
                    style={{ gap: 8 }}
                    textStyle={{ color: "#fff", fontWeight: "bold" }}
                    disabled={false}
                    textProps={{}}
                  />
                </View>
              )}
            </View>
            <View style={styles.controlsContainer}>
              <SortingDropdown
                sortBy={sortBy}
                setSortBy={setSortBy}
                sortDropdownOpen={sortDropdownOpen}
                setSortDropdownOpen={setSortDropdownOpen}
                sortItems={SORT_ITEMS}
                label="Sort by"
              />
              <CustomSearchInput searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
            </View>
          </View>
        </View>
        <CourseRosterTable
          students={getStudentsByCourseIdData?.getStudentsByCourseId.data || []}
          setSelectedStudents={setSelectedStudents}
          selectedStudents={selectedStudents}
        />
        {totalItemsCount > 0 && (
          <TableControlsContainer
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            totalCount={totalItemsCount}
          />
        )}
      </View>
      <NewCustomAxioActionModal
        visible={modalState.type === RosterManagementModalState.REMOVE_STUDENTS}
        onClose={handleModalClose}
        onAction={handleRemoveStudents}
        modalTexts={modals.removeStudents}
      />
      <NewCustomAxioActionModal
        visible={modalState.type === RosterManagementModalState.REMOVE_CONFIRMATION}
        onClose={handleModalClose}
        modalTexts={modals.removeConfirmation}
      />
      <NewCustomAxioActionModal
        visible={modalState.type === RosterManagementModalState.RESEND_INVITES_CONFIRMATION}
        onClose={handleModalClose}
        modalTexts={modals.resendInvitesConfirmation}
      />
      <NewCustomAxioActionModal
        visible={modalState.type === RosterManagementModalState.RESEND_INVITES}
        onClose={handleModalClose}
        modalTexts={modals.resendInvites}
        onAction={handleResendInvites}
      />
      <InviteStudentModalContainer
        visible={modalState.type === RosterManagementModalState.INVITE_STUDENTS}
        onClose={handleModalClose}
        onSubmit={inviteStudents}
        isSubmitting={isSubmitting}
      />
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    padding: 24,
    backgroundColor: "#fff",
    borderRadius: 8,
    flex: 1,
  },
  backButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  backText: {
    color: "#000",
  },
  header: {
    flexDirection: "column",
    width: "100%",
    marginBottom: 24,
    zIndex: 3000,
    elevation: 3000,
  },
  headerRow: {
    flexDirection: "row",
    width: "100%",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 16,
    zIndex: 3000,
    elevation: 3000,
  },
  headerText: {
    color: "#000",
  },
  subHeaderContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  subHeaderText: {
    color: "#000",
  },
  controlsContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
    zIndex: 3000,
    elevation: 3000,
  },
});

export default RosterManagementContainer;
